import styled, { css } from "styled-components";
import { media } from "../../theme/theme";
import { Body, H2, MediumText, SmallText } from "../Typography";
import { MarketingSpacing } from "../ReusableStyledComponents/MarketingSpacing";
import { MCTA } from "../MarketingCTA/MarketingCTA.styles";
import { MarketingPlanKey } from "../../utils/marketingPricing";

/** Regular-weight MediumText for the card body copy (tagline, billing line, bullets,
 *  free note). Local to the pricing block on purpose. */
const MediumTextRegular = styled(MediumText)`
    font-weight: ${({ theme }) => (theme ? theme.typography.weight.regular : null)};
    font-size: 15px;
`;

/* ---------- billing toggle (reused as-is from the previous 2-card block) ---------- */

export const ToggleButtonWrapper = styled.div`
    width: 340px;
    position: relative;
    background: #f1f6ff;
    height: 48px;
    border-radius: 100px;
    margin: 0 auto 50px;

    ${media.phone} {
        width: 380px;
    }
`;

export const ToggleButtonSlider = styled.div<{ plan: "monthly" | "yearly" }>`
    width: 50%;
    height: 100%;
    left: ${({ plan }) => (plan === "yearly" ? "0%" : "50%")};
    transition: left 0.3s ease;
    top: 0;
    background: #ffffff;
    box-shadow: 0px 1px 6px rgba(0, 0, 0, 0.12), -2px 0px 6px rgba(0, 0, 0, 0.06);
    border-radius: 100px;
    position: absolute;
`;

export const ToggleTextLeft = styled(SmallText)`
    width: 50%;
    position: absolute;
    left: 0;
    background: transparent;
    height: 100%;
    line-height: 48px;
    text-align: center;
    color: ${({ theme }) => (theme ? theme.marketing.colors.dodgerBlue100 : null)};

    &:hover {
        cursor: pointer;
    }
`;

export const ToggleTextRight = styled(ToggleTextLeft)`
    left: initial;
    right: 0;
`;

/* ---------- cards grid ---------- */

/** Single centered column on mobile/tablet (capped width like the reference), a
 *  3-up grid on desktop. */
export const PricingMainContentWrapper = styled.div`
    max-width: 420px;
    width: 100%;
    margin: 0 auto;
    display: grid;
    grid-template-columns: 1fr;
    gap: 24px;

    ${MarketingSpacing}

    ${media.desktop} {
        max-width: 1380px;
        grid-template-columns: repeat(3, 1fr);
        align-items: stretch;
    }
`;

export const MargetingPricePanel = styled.div<{ recommended?: boolean; $plan: MarketingPlanKey }>`
    position: relative;
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    background: #ffffff;
    border: ${({ recommended, theme }) =>
        recommended ? `2px solid ${theme.marketing.colors.dodgerBlue100}` : `1px solid ${theme.marketing.colors.dodgerBlue60}`};
    border-top: 5px solid
        ${({ $plan, theme }) => {
            switch ($plan) {
                case "LITE":
                    return theme.v3.colors.grey70;
                case "PRO":
                    return theme.marketing.colors.coral90;
                default:
                    return theme.marketing.colors.dodgerBlue100;
            }
        }};
    box-shadow: 0px 8px 24px rgba(149, 157, 165, 0.13), 0px 1px 4px rgba(0, 0, 0, 0.1);
    border-radius: 6px;
    text-align: center;
    padding: 40px 24px;

    ${media.tablet} {
        padding: 48px 40px;
    }

    ${H2} {
        font-size: 48px;
        margin: 6px 0 2px;
    }
`;

/** Wraps everything above the included list (name, tagline, price, optional free-domain
 *  flag). A desktop min-height reserves space for the tallest header so the included list
 *  starts at the same vertical position across all three cards, regardless of whether the
 *  free-domain flag is present. The free-domain flag only renders on the yearly period, so
 *  the monthly period uses a shorter min-height that matches its (flag-less) tallest header. */
export const CardHeader = styled.div<{ $isYearly: boolean }>`
    ${media.desktop} {
        min-height: ${({ $isYearly }) => ($isYearly ? "255px" : "220px")};
    }
`;

export const RecommendBadge = styled.div`
    position: absolute;
    top: 0;
    left: 50%;
    transform: translate(-50%, -60%);
    background: ${({ theme }) => theme.marketing.colors.dodgerBlue100};
    color: #ffffff;
    font-family: ${({ theme }) => theme.typography.type.assistant};
    font-size: 13px;
    font-weight: 700;
    letter-spacing: 0.5px;
    padding: 8px 20px;
    border-radius: 14px;
    white-space: nowrap;
`;

export const PlanTagline = styled(MediumTextRegular)`
    color: ${({ theme }) => theme.marketing.colors.blackPearl80};
    font-size: 17px;
    line-height: 22px;
    margin: 10px 0 18px;
`;

/** Big price amount and the inline "/month" suffix sit on one baseline-aligned row;
 *  the "billed yearly/monthly" cycle line goes underneath (mirrors the reference). */
export const PriceRow = styled.div`
    display: flex;
    align-items: baseline;
    justify-content: center;
    gap: 4px;
`;

export const PricePer = styled.span`
    font-family: ${({ theme }) => theme.typography.type.assistant};
    font-size: 15px;
    font-weight: 500;
    color: ${({ theme }) => theme.marketing.colors.blackPearl80};
`;

export const PriceCycleText = styled(MediumTextRegular)`
    color: ${({ theme }) => theme.marketing.colors.blackPearl80};
`;

/* ---------- free domain callout ---------- */

export const DomainFlag = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    background: #fff8ec;
    border: 1px solid #fbe6b8;
    border-radius: 9px;
    padding: 9px 12px;
    margin-top: 18px;
    text-align: center;
    font-family: ${({ theme }) => theme.typography.type.assistant};
    font-size: 16px;
    font-weight: 600;
    color: #8a5a00;
`;

export const DomainTooltipAnchor = styled.span`
    display: inline-flex;
    align-items: center;
`;

/** Help-circle trigger sitting just after a bullet label. Nudged down slightly so it lines
 *  up with the first line of the label text (which has a 22px line-height). */
export const BulletTooltipAnchor = styled.span`
    display: inline-flex;
    align-items: center;
    position: relative;
    top: 3px;
`;

/* ---------- included list ---------- */

export const IncludedHeading = styled.div`
    font-family: ${({ theme }) => theme.typography.type.assistant};
    font-size: 16px;
    font-weight: 600;
    text-align: left;
    color: ${({ theme }) => theme.marketing.colors.blackPearl100};
    margin: 22px 0 12px;
`;

/** Kept for reuse by MarketingSteps. */
export const PerksUL = styled.ul`
    text-align: left;

    > li {
        position: relative;
        margin-bottom: 16px;
    }
`;

export const CheckBulletList = styled.ul`
    list-style: none;
    text-align: left;
    margin: 0 0 26px;
    padding: 0;
    flex-grow: 1;
`;

export const CheckBullet = styled(MediumTextRegular)`
    display: flex;
    align-items: flex-start;
    gap: 10px;
    font-size: 17px;
    line-height: 22px;
    color: ${({ theme }) => theme.marketing.colors.blackPearl90};
    padding: 7px 0;

    > svg {
        flex: 0 0 auto;
        position: relative;
        top: 3px;
    }
`;

/* ---------- CTA + free note ---------- */

export const CardCTAWrapper = styled.div<{ $neutralCta?: boolean }>`
    margin-top: auto;
    padding-top: 12px;
    display: flex;
    justify-content: center;

    ${MCTA} {
        padding: 10px 50px;

        ${({ $neutralCta, theme }) =>
            $neutralCta &&
            css`
                background-color: ${theme.marketing.colors.blackPearl40};
                color: ${theme.marketing.colors.blackPearl100};

                &:hover {
                    background-color: ${theme.marketing.colors.blackPearl50};
                }
            `}
    }
`;

export const FreeNoteText = styled(Body)`
    max-width: 860px;
    margin: 0 auto;
    text-align: center;
    font-weight: ${({ theme }) => theme.typography.weight.regular};
    color: ${({ theme }) => theme.marketing.colors.blackPearl80};

    strong {
        font-weight: ${({ theme }) => theme.typography.weight.regular};
        color: ${({ theme }) => theme.marketing.colors.blackPearl100};
    }
`;
